import React, { useEffect, useState } from 'react';
import axios from 'axios';

const errorMessage = (error, fallback) => {
    const errors = error.response?.data?.errors;
    if (errors) {
        const first = Object.values(errors).flat()[0];
        if (first) return first;
    }

    return error.response?.data?.message || fallback;
};

export default function GoogleVisionSettings() {
    const [credentials, setCredentials] = useState({ configured: false });
    const [phrases, setPhrases] = useState([]);
    const [credentialsFile, setCredentialsFile] = useState(null);
    const [newPhrase, setNewPhrase] = useState('');
    const [loading, setLoading] = useState(true);
    const [savingCredentials, setSavingCredentials] = useState(false);
    const [savingPhrase, setSavingPhrase] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    const load = async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get('/api/administrator/google-vision');
            setCredentials(response.data.credentials || { configured: false });
            setPhrases(response.data.phrases || []);
        } catch (requestError) {
            setError(errorMessage(requestError, 'No se pudo cargar la configuración de Google Vision.'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const uploadCredentials = async (event) => {
        event.preventDefault();
        setMessage('');
        setError('');
        if (!credentialsFile) {
            setError('Selecciona el archivo JSON de la cuenta de servicio.');
            return;
        }

        const formData = new FormData();
        formData.append('credentials', credentialsFile);
        setSavingCredentials(true);
        try {
            const response = await axios.post('/api/administrator/google-vision/credentials', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setCredentials(response.data.credentials);
            setCredentialsFile(null);
            event.target.reset();
            setMessage(response.data.message);
        } catch (requestError) {
            setError(errorMessage(requestError, 'No se pudieron guardar las credenciales.'));
        } finally {
            setSavingCredentials(false);
        }
    };

    const addPhrase = async (event) => {
        event.preventDefault();
        setMessage('');
        setError('');
        setSavingPhrase(true);
        try {
            const response = await axios.post('/api/administrator/google-vision/phrases', { phrase: newPhrase });
            setPhrases((current) => [...current, response.data].sort((a, b) => a.phrase.localeCompare(b.phrase)));
            setNewPhrase('');
            setMessage('Frase agregada.');
        } catch (requestError) {
            setError(errorMessage(requestError, 'No se pudo agregar la frase.'));
        } finally {
            setSavingPhrase(false);
        }
    };

    const togglePhrase = async (item) => {
        setMessage('');
        setError('');
        try {
            const response = await axios.patch(`/api/administrator/google-vision/phrases/${item.id}`, {
                active: !item.active,
            });
            setPhrases((current) => current.map((phrase) => phrase.id === item.id ? response.data : phrase));
        } catch (requestError) {
            setError(errorMessage(requestError, 'No se pudo actualizar la frase.'));
        }
    };

    const deletePhrase = async (item) => {
        if (!window.confirm(`¿Eliminar la frase “${item.phrase}”?`)) return;

        setMessage('');
        setError('');
        try {
            await axios.delete(`/api/administrator/google-vision/phrases/${item.id}`);
            setPhrases((current) => current.filter((phrase) => phrase.id !== item.id));
            setMessage('Frase eliminada.');
        } catch (requestError) {
            setError(errorMessage(requestError, 'No se pudo eliminar la frase.'));
        }
    };

    return (
        <div className="card border mb-8">
            <div className="card-header">
                <div className="card-title">
                    <div>
                        <h3 className="card-label mb-1">Google Vision</h3>
                        <span className="text-muted">Credenciales y frases que invalidan una plantilla PDF</span>
                    </div>
                </div>
                <div className="card-toolbar">
                    <a href="/api-google-vision" className="btn btn-sm btn-light-primary">Probar análisis</a>
                </div>
            </div>
            <div className="card-body">
                {error && <div className="alert alert-danger">{error}</div>}
                {message && <div className="alert alert-success">{message}</div>}
                {loading ? <div>Cargando configuración...</div> : (
                    <div className="row">
                        <div className="col-lg-5 mb-6 mb-lg-0">
                            <h5>Cuenta de servicio</h5>
                            <p className="text-muted">
                                Sube el JSON descargado desde Google Cloud. El contenido se almacena cifrado y no se muestra nuevamente.
                            </p>
                            <div className={`alert ${credentials.configured ? 'alert-light-success' : 'alert-light-warning'}`}>
                                <strong>{credentials.configured ? 'Configurada' : 'Sin configurar'}</strong>
                                {credentials.file_name && <div>Archivo: {credentials.file_name}</div>}
                                {credentials.client_email && <div className="text-break">Cuenta: {credentials.client_email}</div>}
                            </div>
                            <form onSubmit={uploadCredentials}>
                                <div className="form-group">
                                    <label>Archivo de credenciales (.json)</label>
                                    <input
                                        type="file"
                                        accept="application/json,.json"
                                        className="form-control"
                                        onChange={(event) => setCredentialsFile(event.target.files?.[0] || null)}
                                    />
                                </div>
                                <button className="btn btn-primary" disabled={savingCredentials}>
                                    {savingCredentials ? 'Guardando...' : credentials.configured ? 'Reemplazar JSON' : 'Guardar JSON'}
                                </button>
                            </form>
                        </div>
                        <div className="col-lg-7">
                            <h5>Frases de invalidez</h5>
                            <p className="text-muted">Si Vision detecta cualquiera de las frases activas, la plantilla se marca como inválida.</p>
                            <form className="d-flex mb-4" onSubmit={addPhrase}>
                                <input
                                    type="text"
                                    className="form-control mr-2"
                                    maxLength="255"
                                    placeholder="Ej. copia sin valor oficial"
                                    value={newPhrase}
                                    onChange={(event) => setNewPhrase(event.target.value)}
                                />
                                <button className="btn btn-success" disabled={savingPhrase || !newPhrase.trim()}>
                                    {savingPhrase ? 'Agregando...' : 'Agregar'}
                                </button>
                            </form>
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered mb-0">
                                    <thead><tr><th>Frase</th><th>Estado</th><th>Acciones</th></tr></thead>
                                    <tbody>
                                        {phrases.length === 0 && <tr><td colSpan="3" className="text-center">No hay frases registradas.</td></tr>}
                                        {phrases.map((item) => (
                                            <tr key={item.id}>
                                                <td>{item.phrase}</td>
                                                <td>{item.active ? 'Activa' : 'Inactiva'}</td>
                                                <td className="text-nowrap">
                                                    <button type="button" className="btn btn-sm btn-light-primary mr-2" onClick={() => togglePhrase(item)}>
                                                        {item.active ? 'Desactivar' : 'Activar'}
                                                    </button>
                                                    <button type="button" className="btn btn-sm btn-light-danger" onClick={() => deletePhrase(item)}>Eliminar</button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
